import { useEffect, useRef, useState } from 'react';

import { resolveWorkspaceUuid } from 'api/meedioBackend/apiKeys/resolveWorkspaceUuid';
import logger from 'utils/logging/faro';

export interface WorkspaceBackendUuidResult {
  workspaceUuid: string | null;
  isLoading: boolean;
  error: unknown;
}

/**
 * Resolves the NestJS workspace UUID for the selected workspace's Matrix room id.
 * The mapping is memoized for the session and re-resolved on workspace switch.
 */
const useWorkspaceBackendUuid = (workspaceMatrixRoomId: string | null): WorkspaceBackendUuidResult => {
  const [workspaceUuid, setWorkspaceUuid] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setWorkspaceUuid(null);
    setError(null);

    if (!workspaceMatrixRoomId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    resolveWorkspaceUuid(workspaceMatrixRoomId)
      .then((uuid) => {
        if (cancelled || !mountedRef.current) return;
        setWorkspaceUuid(uuid);
      })
      .catch((resolveError: unknown) => {
        if (cancelled || !mountedRef.current) return;
        logger.error('Failed to resolve workspace backend UUID', resolveError);
        setError(resolveError);
      })
      .finally(() => {
        if (cancelled || !mountedRef.current) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceMatrixRoomId]);

  return { workspaceUuid, isLoading, error };
};

export default useWorkspaceBackendUuid;
